import {
  createFeedComment,
  createFeedReply,
  deleteFeedComment,
  getFeed,
  getFeedComments,
  getFeedReplies,
  getTrendingFeed,
  likeFeedComment,
  likeFeedItem,
  shareFeedItem,
  unlikeFeedComment,
  unlikeFeedItem,
} from './asyncThunks';

const getItemId = item => item?._id || item?.id || null;

const findFeedItem = (state, feedItemId) =>
  state.getFeedInfo.data.find(item => getItemId(item) === feedItemId) ||
  state.getTrendingInfo.data.find(item => getItemId(item) === feedItemId) ||
  null;

export const getFeedBuilder = builder => {
  builder.addCase(getFeed.pending, (state, { meta }) => {
    state.getFeedInfo.requestId = meta.requestId;

    if (meta.arg?.cursor) {
      state.getFeedInfo.fetchMorePending = true;
    } else {
      state.getFeedInfo.pending = true;
    }
  });

  builder.addCase(getFeed.fulfilled, (state, { payload, meta }) => {
    if (state.getFeedInfo.requestId !== meta.requestId) {
      return;
    }

    if (meta.arg?.cursor) {
      state.getFeedInfo.data = [...state.getFeedInfo.data, ...payload.data];
    } else {
      state.getFeedInfo.data = payload.data;
    }

    state.getFeedInfo.pending = false;
    state.getFeedInfo.fetchMorePending = false;
    state.getFeedInfo.nextCursor = payload.pagination?.nextCursor || null;
    state.getFeedInfo.hasMore = Boolean(payload.pagination?.hasMore);
    state.getFeedInfo.limit = payload.pagination?.limit || meta.arg?.limit || state.getFeedInfo.limit;
    state.getFeedInfo.loaded = true;
  });

  builder.addCase(getFeed.rejected, (state, { meta }) => {
    if (state.getFeedInfo.requestId !== meta.requestId) {
      return;
    }

    state.getFeedInfo.pending = false;
    state.getFeedInfo.fetchMorePending = false;
    state.getFeedInfo.loaded = true;
  });
};

export const getTrendingFeedBuilder = builder => {
  builder.addCase(getTrendingFeed.pending, (state, { meta }) => {
    state.getTrendingInfo.requestId = meta.requestId;

    if (state.getTrendingInfo.data.length) {
      state.getTrendingInfo.fetchMorePending = true;
    } else {
      state.getTrendingInfo.pending = true;
    }
  });

  builder.addCase(getTrendingFeed.fulfilled, (state, { payload, meta }) => {
    if (state.getTrendingInfo.requestId !== meta.requestId) {
      return;
    }

    if (state.getTrendingInfo.data.length) {
      state.getTrendingInfo.data = [...state.getTrendingInfo.data, ...payload.data];
    } else {
      state.getTrendingInfo.data = payload.data;
    }

    state.getTrendingInfo.pending = false;
    state.getTrendingInfo.fetchMorePending = false;
    state.getTrendingInfo.phase = payload.pagination?.phase || state.getTrendingInfo.phase;
    state.getTrendingInfo.cursor = payload.pagination?.cursor || null;
    state.getTrendingInfo.seed = payload.pagination?.seed || null;
    state.getTrendingInfo.hasMore = Boolean(payload.pagination?.hasMore);
    state.getTrendingInfo.limit = payload.pagination?.limit || meta.arg?.limit || state.getTrendingInfo.limit;
    state.getTrendingInfo.loaded = true;
  });

  builder.addCase(getTrendingFeed.rejected, (state, { meta }) => {
    if (state.getTrendingInfo.requestId !== meta.requestId) {
      return;
    }

    state.getTrendingInfo.pending = false;
    state.getTrendingInfo.fetchMorePending = false;
    state.getTrendingInfo.loaded = true;
  });
};

export const likeFeedItemBuilder = builder => {
  builder.addCase(likeFeedItem.pending, (state, { meta }) => {
    const feedItem = findFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = true;
      feedItem.likePending = true;
      feedItem.stats = feedItem.stats || {};
      feedItem.stats.likesCount = Math.max((feedItem.stats.likesCount || 0) + 1, 0);
    }

    state.pending.likeFeedItem = true;
  });

  builder.addCase(likeFeedItem.fulfilled, (state, { payload }) => {
    const feedItem = findFeedItem(state, payload.feedItemId);

    if (feedItem) {
      feedItem.liked = true;
      feedItem.likePending = false;
    }

    state.pending.likeFeedItem = false;
  });

  builder.addCase(likeFeedItem.rejected, (state, { meta }) => {
    const feedItem = findFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = false;
      feedItem.likePending = false;
      feedItem.stats = feedItem.stats || {};
      feedItem.stats.likesCount = Math.max((feedItem.stats.likesCount || 0) - 1, 0);
    }

    state.pending.likeFeedItem = false;
  });
};

export const unlikeFeedItemBuilder = builder => {
  builder.addCase(unlikeFeedItem.pending, (state, { meta }) => {
    const feedItem = findFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = false;
      feedItem.likePending = true;
      feedItem.stats = feedItem.stats || {};
      feedItem.stats.likesCount = Math.max((feedItem.stats.likesCount || 0) - 1, 0);
    }

    state.pending.unlikeFeedItem = true;
  });

  builder.addCase(unlikeFeedItem.fulfilled, (state, { payload }) => {
    const feedItem = findFeedItem(state, payload.feedItemId);

    if (feedItem) {
      feedItem.liked = false;
      feedItem.likePending = false;
    }

    state.pending.unlikeFeedItem = false;
  });

  builder.addCase(unlikeFeedItem.rejected, (state, { meta }) => {
    const feedItem = findFeedItem(state, meta.arg.feedItemId);

    if (feedItem) {
      feedItem.liked = true;
      feedItem.likePending = false;
      feedItem.stats = feedItem.stats || {};
      feedItem.stats.likesCount = Math.max((feedItem.stats.likesCount || 0) + 1, 0);
    }

    state.pending.unlikeFeedItem = false;
  });
};

export const shareFeedItemBuilder = builder => {
  builder.addCase(shareFeedItem.pending, state => {
    state.pending.shareFeedItem = true;
  });

  builder.addCase(shareFeedItem.fulfilled, (state, { payload }) => {
    const feedItem = findFeedItem(state, payload.feedItemId);

    if (feedItem) {
      feedItem.stats = feedItem.stats || {};
      feedItem.stats.sharesCount = Math.max((feedItem.stats.sharesCount || 0) + 1, 0);
    }

    state.pending.shareFeedItem = false;
  });

  builder.addCase(shareFeedItem.rejected, state => {
    state.pending.shareFeedItem = false;
  });
};

export const getFeedCommentsBuilder = builder => {
  builder.addCase(getFeedComments.pending, (state, { meta }) => {
    if (!state.commentsByFeedId[meta.arg.feedItemId]) {
      state.commentsByFeedId[meta.arg.feedItemId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    if (meta.arg?.cursor) {
      state.commentsByFeedId[meta.arg.feedItemId].fetchMorePending = true;
    } else {
      state.commentsByFeedId[meta.arg.feedItemId].pending = true;
    }
  });

  builder.addCase(getFeedComments.fulfilled, (state, { payload, meta }) => {
    if (!state.commentsByFeedId[payload.feedItemId]) {
      state.commentsByFeedId[payload.feedItemId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    if (meta.arg?.cursor) {
      state.commentsByFeedId[payload.feedItemId].data = [
        ...state.commentsByFeedId[payload.feedItemId].data,
        ...payload.data,
      ];
    } else {
      state.commentsByFeedId[payload.feedItemId].data = payload.data;
    }

    state.commentsByFeedId[payload.feedItemId].pending = false;
    state.commentsByFeedId[payload.feedItemId].fetchMorePending = false;
    state.commentsByFeedId[payload.feedItemId].nextCursor = payload.pagination?.nextCursor || null;
    state.commentsByFeedId[payload.feedItemId].hasMore = Boolean(payload.pagination?.hasMore);
    state.commentsByFeedId[payload.feedItemId].limit =
      payload.pagination?.limit || meta.arg?.limit || state.commentsByFeedId[payload.feedItemId].limit;
    state.commentsByFeedId[payload.feedItemId].loaded = true;
  });

  builder.addCase(getFeedComments.rejected, (state, { meta }) => {
    if (!state.commentsByFeedId[meta.arg.feedItemId]) {
      state.commentsByFeedId[meta.arg.feedItemId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    state.commentsByFeedId[meta.arg.feedItemId].pending = false;
    state.commentsByFeedId[meta.arg.feedItemId].fetchMorePending = false;
    state.commentsByFeedId[meta.arg.feedItemId].loaded = true;
  });
};

export const createFeedCommentBuilder = builder => {
  builder.addCase(createFeedComment.pending, (state, { meta }) => {
    if (!state.commentsByFeedId[meta.arg.feedItemId]) {
      state.commentsByFeedId[meta.arg.feedItemId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    state.commentsByFeedId[meta.arg.feedItemId].createPending = true;
    state.pending.createFeedComment = true;
  });

  builder.addCase(createFeedComment.fulfilled, (state, { payload }) => {
    if (!state.commentsByFeedId[payload.feedItemId]) {
      state.commentsByFeedId[payload.feedItemId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    const commentId = payload.comment?._id || payload.comment?.id || null;
    const exists = state.commentsByFeedId[payload.feedItemId].data.some(
      comment => (comment?._id || comment?.id || null) === commentId,
    );

    if (!exists) {
      state.commentsByFeedId[payload.feedItemId].data = [
        payload.comment,
        ...state.commentsByFeedId[payload.feedItemId].data,
      ];
    }

    state.commentsByFeedId[payload.feedItemId].createPending = false;
    state.commentsByFeedId[payload.feedItemId].loaded = true;

    const feedItem = findFeedItem(state, payload.feedItemId);

    if (feedItem) {
      feedItem.stats = feedItem.stats || {};
      feedItem.stats.commentsCount = Math.max((feedItem.stats.commentsCount || 0) + 1, 0);
    }

    state.pending.createFeedComment = false;
  });

  builder.addCase(createFeedComment.rejected, (state, { meta }) => {
    if (!state.commentsByFeedId[meta.arg.feedItemId]) {
      state.commentsByFeedId[meta.arg.feedItemId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    state.commentsByFeedId[meta.arg.feedItemId].createPending = false;
    state.pending.createFeedComment = false;
  });
};

export const getFeedRepliesBuilder = builder => {
  builder.addCase(getFeedReplies.pending, (state, { meta }) => {
    if (!state.repliesByCommentId[meta.arg.feedCommentId]) {
      state.repliesByCommentId[meta.arg.feedCommentId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    if (meta.arg?.cursor) {
      state.repliesByCommentId[meta.arg.feedCommentId].fetchMorePending = true;
    } else {
      state.repliesByCommentId[meta.arg.feedCommentId].pending = true;
    }
  });

  builder.addCase(getFeedReplies.fulfilled, (state, { payload, meta }) => {
    if (!state.repliesByCommentId[payload.feedCommentId]) {
      state.repliesByCommentId[payload.feedCommentId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    if (meta.arg?.cursor) {
      state.repliesByCommentId[payload.feedCommentId].data = [
        ...state.repliesByCommentId[payload.feedCommentId].data,
        ...payload.data,
      ];
    } else {
      state.repliesByCommentId[payload.feedCommentId].data = payload.data;
    }

    state.repliesByCommentId[payload.feedCommentId].pending = false;
    state.repliesByCommentId[payload.feedCommentId].fetchMorePending = false;
    state.repliesByCommentId[payload.feedCommentId].nextCursor = payload.pagination?.nextCursor || null;
    state.repliesByCommentId[payload.feedCommentId].hasMore = Boolean(payload.pagination?.hasMore);
    state.repliesByCommentId[payload.feedCommentId].limit =
      payload.pagination?.limit || meta.arg?.limit || state.repliesByCommentId[payload.feedCommentId].limit;
    state.repliesByCommentId[payload.feedCommentId].loaded = true;
  });

  builder.addCase(getFeedReplies.rejected, (state, { meta }) => {
    if (!state.repliesByCommentId[meta.arg.feedCommentId]) {
      state.repliesByCommentId[meta.arg.feedCommentId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    state.repliesByCommentId[meta.arg.feedCommentId].pending = false;
    state.repliesByCommentId[meta.arg.feedCommentId].fetchMorePending = false;
    state.repliesByCommentId[meta.arg.feedCommentId].loaded = true;
  });
};

export const createFeedReplyBuilder = builder => {
  builder.addCase(createFeedReply.pending, state => {
    state.pending.createFeedReply = true;
  });

  builder.addCase(createFeedReply.fulfilled, (state, { payload }) => {
    const comment = payload.comment;
    const parentCommentId =
      comment.parentComment?._id || comment.parentComment?.id || null || comment.parentComment || payload.feedCommentId;
    const feedItemId = comment.feedItem?._id || comment.feedItem?.id || null || comment.feedItem || payload.feedItemId;

    if (!state.repliesByCommentId[parentCommentId]) {
      state.repliesByCommentId[parentCommentId] = {
        data: [],
        nextCursor: null,
        hasMore: true,
        limit: 20,
        pending: false,
        fetchMorePending: false,
        loaded: false,
        createPending: false,
      };
    }

    const commentId = comment?._id || comment?.id || null;
    const exists = state.repliesByCommentId[parentCommentId].data.some(
      item => (item?._id || item?.id || null) === commentId,
    );

    if (!exists) {
      state.repliesByCommentId[parentCommentId].data = [...state.repliesByCommentId[parentCommentId].data, comment];
    }

    state.repliesByCommentId[parentCommentId].fetchMorePending = false;
    state.repliesByCommentId[parentCommentId].loaded = true;

    let parentComment = null;

    for (const feedItemIdKey of Object.keys(state.commentsByFeedId)) {
      parentComment = state.commentsByFeedId[feedItemIdKey].data.find(
        item => (item?._id || item?.id || null) === parentCommentId,
      );

      if (parentComment) {
        break;
      }
    }

    if (!parentComment) {
      for (const parentCommentIdKey of Object.keys(state.repliesByCommentId)) {
        parentComment = state.repliesByCommentId[parentCommentIdKey].data.find(
          item => (item?._id || item?.id || null) === parentCommentId,
        );

        if (parentComment) {
          break;
        }
      }
    }

    if (parentComment) {
      parentComment.repliesCount = (parentComment.repliesCount || 0) + 1;
    }

    const feedItem = findFeedItem(state, feedItemId);

    if (feedItem) {
      feedItem.stats = feedItem.stats || {};
      feedItem.stats.commentsCount = Math.max((feedItem.stats.commentsCount || 0) + 1, 0);
    }

    state.pending.createFeedReply = false;
  });

  builder.addCase(createFeedReply.rejected, state => {
    state.pending.createFeedReply = false;
  });
};

export const likeFeedCommentBuilder = builder => {
  builder.addCase(likeFeedComment.pending, (state, { meta }) => {
    let comment = null;

    for (const feedItemId of Object.keys(state.commentsByFeedId)) {
      comment = state.commentsByFeedId[feedItemId].data.find(
        item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
      );

      if (comment) {
        break;
      }
    }

    if (!comment) {
      for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
        comment = state.repliesByCommentId[parentCommentId].data.find(
          item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
        );

        if (comment) {
          break;
        }
      }
    }

    if (comment) {
      comment.liked = true;
      comment.likePending = true;
      comment.likesCount = Math.max((comment.likesCount || 0) + 1, 0);
    }

    state.pending.likeFeedComment = true;
  });

  builder.addCase(likeFeedComment.fulfilled, (state, { payload }) => {
    let comment = null;

    for (const feedItemId of Object.keys(state.commentsByFeedId)) {
      comment = state.commentsByFeedId[feedItemId].data.find(
        item => (item?._id || item?.id || null) === payload.feedCommentId,
      );

      if (comment) {
        break;
      }
    }

    if (!comment) {
      for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
        comment = state.repliesByCommentId[parentCommentId].data.find(
          item => (item?._id || item?.id || null) === payload.feedCommentId,
        );

        if (comment) {
          break;
        }
      }
    }

    if (comment) {
      comment.liked = true;
      comment.likePending = false;
    }

    state.pending.likeFeedComment = false;
  });

  builder.addCase(likeFeedComment.rejected, (state, { meta }) => {
    let comment = null;

    for (const feedItemId of Object.keys(state.commentsByFeedId)) {
      comment = state.commentsByFeedId[feedItemId].data.find(
        item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
      );

      if (comment) {
        break;
      }
    }

    if (!comment) {
      for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
        comment = state.repliesByCommentId[parentCommentId].data.find(
          item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
        );

        if (comment) {
          break;
        }
      }
    }

    if (comment) {
      comment.liked = false;
      comment.likePending = false;
      comment.likesCount = Math.max((comment.likesCount || 0) - 1, 0);
    }

    state.pending.likeFeedComment = false;
  });
};

export const unlikeFeedCommentBuilder = builder => {
  builder.addCase(unlikeFeedComment.pending, (state, { meta }) => {
    let comment = null;

    for (const feedItemId of Object.keys(state.commentsByFeedId)) {
      comment = state.commentsByFeedId[feedItemId].data.find(
        item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
      );

      if (comment) {
        break;
      }
    }

    if (!comment) {
      for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
        comment = state.repliesByCommentId[parentCommentId].data.find(
          item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
        );

        if (comment) {
          break;
        }
      }
    }

    if (comment) {
      comment.liked = false;
      comment.likePending = true;
      comment.likesCount = Math.max((comment.likesCount || 0) - 1, 0);
    }

    state.pending.unlikeFeedComment = true;
  });

  builder.addCase(unlikeFeedComment.fulfilled, (state, { payload }) => {
    let comment = null;

    for (const feedItemId of Object.keys(state.commentsByFeedId)) {
      comment = state.commentsByFeedId[feedItemId].data.find(
        item => (item?._id || item?.id || null) === payload.feedCommentId,
      );

      if (comment) {
        break;
      }
    }

    if (!comment) {
      for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
        comment = state.repliesByCommentId[parentCommentId].data.find(
          item => (item?._id || item?.id || null) === payload.feedCommentId,
        );

        if (comment) {
          break;
        }
      }
    }

    if (comment) {
      comment.liked = false;
      comment.likePending = false;
    }

    state.pending.unlikeFeedComment = false;
  });

  builder.addCase(unlikeFeedComment.rejected, (state, { meta }) => {
    let comment = null;

    for (const feedItemId of Object.keys(state.commentsByFeedId)) {
      comment = state.commentsByFeedId[feedItemId].data.find(
        item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
      );

      if (comment) {
        break;
      }
    }

    if (!comment) {
      for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
        comment = state.repliesByCommentId[parentCommentId].data.find(
          item => (item?._id || item?.id || null) === meta.arg.feedCommentId,
        );

        if (comment) {
          break;
        }
      }
    }

    if (comment) {
      comment.liked = true;
      comment.likePending = false;
      comment.likesCount = Math.max((comment.likesCount || 0) + 1, 0);
    }

    state.pending.unlikeFeedComment = false;
  });
};

export const deleteFeedCommentBuilder = builder => {
  builder.addCase(deleteFeedComment.pending, state => {
    state.pending.deleteFeedComment = true;
  });

  builder.addCase(deleteFeedComment.fulfilled, (state, { payload }) => {
    let removedInfo = null;

    for (const feedItemId of Object.keys(state.commentsByFeedId)) {
      const commentIndex = state.commentsByFeedId[feedItemId].data.findIndex(
        item => (item?._id || item?.id || null) === payload.feedCommentId,
      );

      if (commentIndex !== -1) {
        const [removedComment] = state.commentsByFeedId[feedItemId].data.splice(commentIndex, 1);
        const removedRepliesCount = removedComment.repliesCount ?? 0;

        delete state.repliesByCommentId[payload.feedCommentId];

        removedInfo = {
          feedItemId,
          removedCount: 1 + removedRepliesCount,
          parentCommentId: null,
        };
        break;
      }
    }

    if (!removedInfo) {
      for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
        const commentIndex = state.repliesByCommentId[parentCommentId].data.findIndex(
          item => (item?._id || item?.id || null) === payload.feedCommentId,
        );

        if (commentIndex !== -1) {
          const [removedComment] = state.repliesByCommentId[parentCommentId].data.splice(commentIndex, 1);

          removedInfo = {
            feedItemId:
              removedComment.feedItem?._id || removedComment.feedItem?.id || null || removedComment.feedItem || null,
            removedCount: 1,
            parentCommentId,
          };
          break;
        }
      }
    }

    if (removedInfo?.parentCommentId) {
      let parentComment = null;

      for (const feedItemId of Object.keys(state.commentsByFeedId)) {
        parentComment = state.commentsByFeedId[feedItemId].data.find(
          item => (item?._id || item?.id || null) === removedInfo.parentCommentId,
        );

        if (parentComment) {
          break;
        }
      }

      if (!parentComment) {
        for (const parentCommentId of Object.keys(state.repliesByCommentId)) {
          parentComment = state.repliesByCommentId[parentCommentId].data.find(
            item => (item?._id || item?.id || null) === removedInfo.parentCommentId,
          );

          if (parentComment) {
            break;
          }
        }
      }

      if (parentComment) {
        parentComment.repliesCount = Math.max((parentComment.repliesCount || 0) - 1, 0);
      }
    }

    if (removedInfo?.feedItemId) {
      const feedItem = findFeedItem(state, removedInfo.feedItemId);

      if (feedItem) {
        feedItem.stats = feedItem.stats || {};
        feedItem.stats.commentsCount = Math.max((feedItem.stats.commentsCount || 0) - removedInfo.removedCount, 0);
      }
    }

    state.pending.deleteFeedComment = false;
  });

  builder.addCase(deleteFeedComment.rejected, state => {
    state.pending.deleteFeedComment = false;
  });
};
